// Section 21: facts the Ledger derives from state beyond the accumulator: what became of each
// person, life expectancy by origin, the fate of each origin group at the gate, every justice
// case, steady years and the event marks on the fragility chart. Pure, sorted iteration.
import { mean } from '../../core/math';
import { sortedValues } from '../../core/sorted';
import type {
  EventId,
  GameState,
  LedgerCase,
  LedgerFate,
  LedgerFragilityMark,
  LedgerGroupFate,
  Origin,
  PersonId,
  SteadyResult,
} from '../../core/types';

export const DEPRIVATION_CAUSES = new Set(['starvation', 'dehydration', 'exposure']);

export interface PersonFate {
  fate: LedgerFate;
  /** Turn of death or departure; null for the living. */
  turn: number | null;
}

export function fateOf(state: GameState, id: PersonId): PersonFate {
  if (state.people[id] !== undefined) return { fate: 'alive', turn: null };
  const former = state.formerPeople[id];
  if (former === undefined) return { fate: 'left', turn: null };
  switch (former.fate.kind) {
    case 'died':
      return { fate: 'died', turn: former.turn };
    case 'left':
      return { fate: 'left', turn: former.turn };
    case 'exiled':
      return { fate: 'exiled', turn: former.turn };
  }
}

export function nameOf(state: GameState, id: PersonId): string {
  return state.people[id]?.name ?? state.formerPeople[id]?.name ?? id;
}

interface Member {
  id: PersonId;
  origin: Origin;
  ageSeasons: number;
  fate: LedgerFate;
  trust: number | null;
}

/** Everyone who ever lived in the settlement, the living first then the former, by id. */
export function everyone(state: GameState): Member[] {
  const out: Member[] = [];
  for (const p of sortedValues(state.people))
    out.push({
      id: p.id,
      origin: p.origin,
      ageSeasons: p.ageSeasons,
      fate: 'alive',
      trust: p.trust,
    });
  for (const f of sortedValues(state.formerPeople)) {
    if (state.people[f.id] !== undefined) continue;
    out.push({
      id: f.id,
      origin: f.origin,
      ageSeasons: f.ageSeasons,
      fate: fateOf(state, f.id).fate,
      trust: null,
    });
  }
  return out;
}

/**
 * Life expectancy by origin as the mean observed lifespan (G-04): age at death for the dead,
 * age on leaving for those who left, current age for the living. A lower bound while people
 * live, and the same measure for every group.
 */
export function lifeExpectancy(state: GameState): Partial<Record<Origin, number>> {
  const by: Partial<Record<Origin, number[]>> = {};
  for (const m of everyone(state)) (by[m.origin] ??= []).push(m.ageSeasons);
  const out: Partial<Record<Origin, number>> = {};
  for (const origin of ['founder', 'born', 'newcomer'] as const) {
    const ages = by[origin];
    if (ages !== undefined && ages.length > 0) out[origin] = mean(ages);
  }
  return out;
}

/** Seasons each founder and newcomer has lived in the settlement (to now, or to their end). */
function seasonsHere(state: GameState): { origin: Origin; seasons: number }[] {
  const arrived = new Map<PersonId, number>();
  for (const a of state.ledger.arrivals) for (const id of a.admittedIds) arrived.set(id, a.turn);
  const out: { origin: Origin; seasons: number }[] = [];
  const since = (id: PersonId, origin: Origin, arrivedTurn: number | null): number | undefined =>
    origin === 'founder' ? 0 : origin === 'newcomer' ? (arrivedTurn ?? arrived.get(id)) : undefined;
  for (const p of sortedValues(state.people)) {
    const from = since(p.id, p.origin, p.arrivedTurn);
    if (from !== undefined) out.push({ origin: p.origin, seasons: state.turn - from });
  }
  for (const f of sortedValues(state.formerPeople)) {
    if (state.people[f.id] !== undefined) continue;
    const from = since(f.id, f.origin, null);
    if (from !== undefined) out.push({ origin: f.origin, seasons: f.turn - from });
  }
  return out;
}

/**
 * Newcomers who have lived at least `minSeasons` in the settlement: from their arrival to their
 * death or leaving, or to now for the living (M7, G-04).
 */
export function settledNewcomers(state: GameState, minSeasons: number): number {
  return seasonsHere(state).filter((m) => m.origin === 'newcomer' && m.seasons >= minSeasons)
    .length;
}

export interface EarlyDeaths {
  /** Deaths other than of old age. */
  deaths: number;
  /** Seasons lived in the settlement. */
  seasons: number;
  rate: number;
}

/**
 * M7 (G-04): deaths other than of old age per season lived in the settlement, for founders and
 * newcomers. Life expectancy runs inversely to this rate, and unlike observed lifespans (ages)
 * it does not favour the older founding cohort, so the Founders' club tag reads it: newcomers
 * whose life expectancy is 10% below founders' die early at 1 / 0.9 of their rate or more.
 */
export function earlyDeaths(state: GameState): Partial<Record<Origin, EarlyDeaths>> {
  const out: Partial<Record<Origin, EarlyDeaths>> = {};
  for (const m of seasonsHere(state)) {
    const row = (out[m.origin] ??= { deaths: 0, seasons: 0, rate: 0 });
    row.seasons += m.seasons;
  }
  for (const d of state.ledger.deaths) {
    const row = out[d.origin];
    if (row !== undefined && d.cause !== 'old-age') row.deaths++;
  }
  for (const row of Object.values(out)) row.rate = row.seasons > 0 ? row.deaths / row.seasons : 0;
  return out;
}

export function meanAgeAtDeath(state: GameState): Partial<Record<Origin, number>> {
  const out: Partial<Record<Origin, number>> = {};
  for (const origin of ['founder', 'born', 'newcomer'] as const) {
    const ages = state.ledger.deaths.filter((d) => d.origin === origin).map((d) => d.ageSeasons);
    if (ages.length > 0) out[origin] = mean(ages);
  }
  return out;
}

export function groupFate(state: GameState, origin: Origin): LedgerGroupFate {
  const members = everyone(state).filter((m) => m.origin === origin);
  const trust = members.filter((m) => m.trust !== null).map((m) => m.trust as number);
  let short = 0;
  for (const m of members) short += state.ledger.wentWithout[m.id]?.seasonsShort ?? 0;
  return {
    people: members.length,
    alive: members.filter((m) => m.fate === 'alive').length,
    died: members.filter((m) => m.fate === 'died').length,
    left: members.filter((m) => m.fate === 'left' || m.fate === 'exiled').length,
    lifeExpectancySeasons: members.length === 0 ? null : mean(members.map((m) => m.ageSeasons)),
    meanSeasonsShort: members.length === 0 ? 0 : short / members.length,
    trustAtEnd: trust.length === 0 ? null : mean(trust),
  };
}

export function caseRows(state: GameState): LedgerCase[] {
  return sortedValues(state.cases).map((c) => {
    const f = fateOf(state, c.offenderId);
    return {
      caseId: c.id,
      turn: c.turn,
      crime: c.crime,
      offenderId: c.offenderId,
      offenderName: nameOf(state, c.offenderId),
      response: c.response,
      reoffended: c.reoffended,
      fate: f.fate,
      fateAfterSeasons: f.turn === null ? null : Math.max(0, f.turn - c.turn),
    };
  });
}

/** Every Section 20 result over the run: full reports plus the verbatim key entries of summaries. */
export function steadyResults(state: GameState): SteadyResult[] {
  const out: SteadyResult[] = [];
  for (const s of state.historySummaries)
    for (const e of s.keyEntries) if (e.kind === 'steady') out.push(e.result);
  for (const r of state.history) if (r.steady !== null) out.push(r.steady);
  return out.sort((a, b) => a.turn - b.turn);
}

/** Seasons with events, oldest first (summaries keep the count, full reports the ids). */
export function fragilityMarks(state: GameState): LedgerFragilityMark[] {
  const marks: LedgerFragilityMark[] = [];
  for (const s of state.historySummaries) {
    const count = s.entryCounts.event ?? 0;
    if (count > 0) marks.push({ turn: s.turn, eventIds: [], count });
  }
  for (const r of state.history) {
    const eventIds: EventId[] = [];
    for (const e of r.entries) if (e.kind === 'event') eventIds.push(e.eventId);
    if (eventIds.length > 0) marks.push({ turn: r.turn, eventIds, count: eventIds.length });
  }
  return marks.sort((a, b) => a.turn - b.turn);
}

/**
 * Winters with a food shortage (the full reports know the resource; a compacted Winter
 * counts when it had any shortage entry).
 */
export function hungryWinters(state: GameState): number {
  let n = 0;
  for (const s of state.historySummaries)
    if (s.season === 'winter' && (s.entryCounts.shortage ?? 0) > 0) n++;
  for (const r of state.history) {
    if (r.season !== 'winter') continue;
    if (r.entries.some((e) => e.kind === 'shortage' && e.resource === 'food')) n++;
  }
  return n;
}

/** Peak of a per-season series and the turn it happened (series index i is turn i + 1). */
export function peakOf(series: readonly number[]): { peak: number; turn: number } {
  let peak = 0;
  let turn = 0;
  series.forEach((v, i) => {
    if (v > peak) {
      peak = v;
      turn = i + 1;
    }
  });
  return { peak, turn };
}

/** The 1-based year a turn falls in. */
export function yearOfTurn(turn: number, seasonsPerYear: number): number {
  return Math.floor((Math.max(1, turn) - 1) / seasonsPerYear) + 1;
}
